import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './context/AuthContext.jsx';
import { SidebarProvider } from './context/SidebarContext.jsx';
import { ThemeProvider } from './context/ThemeContext.jsx';
import { NotificationProvider } from './context/NotificationContext.jsx';
import PrivateRoute from './components/common/PrivateRoute.jsx';
import Navbar from './components/common/Navbar.jsx';
import Sidebar from './components/common/Sidebar.jsx';

import LayoutWrapper from './components/common/LayoutWrapper.jsx';

// Pages
import Home from './pages/Home.jsx';
import Login from './pages/auth/Login.jsx';
import Register from './pages/auth/Register.jsx';
import ForgotPassword from './pages/auth/ForgotPassword.jsx';
import ResetPassword from './pages/auth/ResetPassword.jsx';
import Courses from './pages/courses/Courses.jsx';
import CourseDetail from './pages/courses/CourseDetail.jsx';
import VideoCoursesPage from './pages/courses/VideoCoursesPage.jsx';
import Dashboard from './pages/dashboard/Dashboard.jsx';
import MyCourses from './pages/student/MyCourses.jsx';
import StudentDashboard from './pages/student/StudentDashboard.jsx';
import CoursePlayer from './pages/student/CoursePlayer.jsx';
import InstructorDashboard from './pages/instructor/InstructorDashboard.jsx';
import CreateCourse from './pages/instructor/CreateCourse.jsx';
import EditCourse from './pages/instructor/EditCourse.jsx';
import AdminDashboard from './pages/admin/AdminDashboard.jsx';
import AdminWebinars from './pages/admin/AdminWebinars.jsx';
import AIUsage from './pages/admin/AIUsage.jsx';
import Analytics from './pages/admin/Analytics.jsx';
import ManageTemplates from './pages/admin/ManageTemplates.jsx';
import NotificationPanel from './pages/admin/NotificationPanel.jsx';
import AdminPanel from './pages/AdminPanel.jsx';
import Chat from './components/Chat.jsx';
import KnowledgeBase from './components/KnowledgeBase.jsx';
import AdminSupport from './components/AdminSupport.jsx';
import CommunityLanding from './components/CommunityLanding.jsx';
import NotificationDemo from './pages/NotificationDemo.jsx';

// AI Trading Pages
import AiTradingDashboard from './pages/ai-trading/AiTradingDashboard.jsx';
import AnalyzePage from './pages/ai-trading/AnalyzePage.jsx';
import StockAnalysisPage from './pages/ai-trading/StockAnalysisPage.jsx';
import HistoryPage from './pages/ai-trading/HistoryPage.jsx';
import Settings from './pages/Settings.jsx';
import StudentSettings from './components/StudentSettings.jsx';
import Help from './pages/Help.jsx';
import Notifications from './pages/Notifications.jsx';
import WireframeDemo from './pages/demo/WireframeDemo.jsx';
import WebinarsPage from './pages/webinars/WebinarsPage.jsx';
import WebinarDetail from './pages/webinars/WebinarDetail.jsx';
import CoderAI from './pages/CoderAI.jsx';
import Templates from './pages/coder/Templates.jsx';
import PaymentSuccess from './pages/payment/PaymentSuccess.jsx';
import PaymentFailed from './pages/payment/PaymentFailed.jsx';
import PaymentCheckout from './pages/payment/PaymentCheckout.jsx';
import Wallpapers from './pages/Wallpapers.jsx';
import TradingviewDashboard from './pages/TradingviewDashboard.jsx';
import AdminIndicatorAlerts from './pages/AdminIndicatorAlerts.jsx';
import AdminFolders from './pages/AdminFolders.jsx';
import AdminAlerts from './pages/AdminAlerts.jsx';
import AdminUsers from './pages/AdminUsers.jsx';
import AdminTerms from './pages/AdminTerms.jsx';
import AlertReceiver from './pages/AlertReceiver.jsx';

// SISTEM Pages
import IndicatorCoursesPage from './pages/sistem/IndicatorCoursesPage.jsx';
import IndicatorDetailPage from './pages/sistem/IndicatorDetailPage.jsx';
import EACopierPage from './pages/sistem/EACopierPage.jsx';

// Tradingview & FCPO Pages
import TradingviewLaptopPage from './pages/tradingview/TradingviewLaptopPage.jsx';
import TradingviewMobilePage from './pages/tradingview/TradingviewMobilePage.jsx';
import FCPOPage from './pages/tradingview/FCPOPage.jsx';

// Force Rebuild Trigger
function App() {
  const LoadingFallback = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#0f172a'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '5px solid rgba(59, 130, 246, 0.3)',
          borderTop: '5px solid rgb(59, 130, 246)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <p style={{ color: '#94a3b8', fontSize: '16px' }}>Loading...</p>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <SidebarProvider>
            <Router>
              <React.Suspense fallback={<LoadingFallback />}>
                <LayoutWrapper>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:resetToken" element={<ResetPassword />} />
                    <Route path="/courses" element={<Courses />} />
                    <Route path="/video-courses" element={<VideoCoursesPage />} />
                    <Route path="/courses/:id" element={<CourseDetail />} />
                    <Route path="/webinars" element={<WebinarsPage />} />
                    <Route path="/webinars/:id" element={<WebinarDetail />} />
                    <Route path="/live-streaming" element={<WebinarsPage />} />

                    {/* Student Routes */}
                    <Route path="/dashboard" element={
                      <PrivateRoute>
                        <Dashboard />
                      </PrivateRoute>
                    } />
                    <Route path="/my-courses" element={
                      <PrivateRoute roles={['student']}>
                        <MyCourses />
                      </PrivateRoute>
                    } />
                    <Route path="/student/dashboard" element={
                      <PrivateRoute roles={['student']}>
                        <StudentDashboard />
                      </PrivateRoute>
                    } />
                    <Route path="/courses/:courseId/learn" element={
                      <PrivateRoute roles={['student']}>
                        <CoursePlayer />
                      </PrivateRoute>
                    } />

                    {/* Instructor Routes */}
                    <Route path="/instructor/dashboard" element={
                      <PrivateRoute roles={['instructor', 'admin']}>
                        <InstructorDashboard />
                      </PrivateRoute>
                    } />
                    <Route path="/instructor/courses" element={
                      <PrivateRoute roles={['instructor', 'admin']}>
                        <InstructorDashboard />
                      </PrivateRoute>
                    } />
                    <Route path="/instructor/courses/create" element={
                      <PrivateRoute roles={['instructor', 'admin']}>
                        <CreateCourse />
                      </PrivateRoute>
                    } />
                    <Route path="/instructor/courses/:id/edit" element={
                      <PrivateRoute roles={['instructor', 'admin']}>
                        <EditCourse />
                      </PrivateRoute>
                    } />

                    {/* AI Trading Routes */}
                    <Route path="/ai-trading" element={
                      <PrivateRoute>
                        <AiTradingDashboard />
                      </PrivateRoute>
                    } />
                    <Route path="/ai-trading/analyze" element={
                      <PrivateRoute>
                        <AnalyzePage />
                      </PrivateRoute>
                    } />
                    <Route path="/ai-trading/stock-analysis" element={
                      <PrivateRoute>
                        <StockAnalysisPage />
                      </PrivateRoute>
                    } />
                    <Route path="/ai-trading/history" element={
                      <PrivateRoute>
                        <HistoryPage />
                      </PrivateRoute>
                    } />

                    {/* SISTEM Routes */}
                    <Route path="/sistem/indicator" element={
                      <PrivateRoute>
                        <IndicatorCoursesPage />
                      </PrivateRoute>
                    } />
                    <Route path="/sistem/indicator/:indicatorId" element={
                      <PrivateRoute>
                        <IndicatorDetailPage />
                      </PrivateRoute>
                    } />
                    <Route path="/sistem/ea-copier" element={
                      <PrivateRoute>
                        <EACopierPage />
                      </PrivateRoute>
                    } />

                    {/* TradingView & FCPO Routes */}
                    <Route path="/tradingview/laptop" element={
                      <PrivateRoute>
                        <TradingviewLaptopPage />
                      </PrivateRoute>
                    } />
                    <Route path="/tradingview/mobile" element={
                      <PrivateRoute>
                        <TradingviewMobilePage />
                      </PrivateRoute>
                    } />
                    <Route path="/tradingview/fcpo" element={
                      <PrivateRoute>
                        <FCPOPage />
                      </PrivateRoute>
                    } />

                    {/* Admin Routes */}
                    <Route path="/admin/dashboard" element={
                      <PrivateRoute roles={['admin']}>
                        <AdminDashboard />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/webinars" element={
                      <PrivateRoute roles={['admin']}>
                        <AdminWebinars />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/ai-usage" element={
                      <PrivateRoute roles={['admin']}>
                        <AIUsage />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/analytics" element={
                      <PrivateRoute roles={['admin']}>
                        <Analytics />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/templates" element={
                      <PrivateRoute roles={['admin']}>
                        <ManageTemplates />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/notifications" element={
                      <PrivateRoute roles={['admin']}>
                        <NotificationPanel />
                      </PrivateRoute>
                    } />
                    <Route path="/admin-panel" element={
                      <PrivateRoute roles={['admin']}>
                        <AdminPanel />
                      </PrivateRoute>
                    } />

                    {/* General Routes */}
                    <Route path="/settings" element={
                      <PrivateRoute>
                        <Settings />
                      </PrivateRoute>
                    } />
                    <Route path="/notifications" element={
                      <PrivateRoute>
                        <Notifications />
                      </PrivateRoute>
                    } />
                    <Route path="/student/settings" element={
                      <PrivateRoute>
                        <StudentSettings />
                      </PrivateRoute>
                    } />
                    <Route path="/help" element={
                      <PrivateRoute>
                        <Help />
                      </PrivateRoute>
                    } />
                    <Route path="/wallpapers" element={
                      <PrivateRoute>
                        <Wallpapers />
                      </PrivateRoute>
                    } />
                    <Route path="/community" element={
                      <PrivateRoute>
                        <CommunityLanding />
                      </PrivateRoute>
                    } />
                    <Route path="/chat" element={
                      <PrivateRoute>
                        <Chat />
                      </PrivateRoute>
                    } />
                    <Route path="/knowledge-base" element={
                      <PrivateRoute>
                        <KnowledgeBase />
                      </PrivateRoute>
                    } />
                    <Route path="/admin-support" element={
                      <PrivateRoute>
                        <AdminSupport />
                      </PrivateRoute>
                    } />
                    <Route path="/notification-demo" element={
                      <PrivateRoute>
                        <NotificationDemo />
                      </PrivateRoute>
                    } />
                    {/* TradingView System */}
                    <Route path="/tradingview-alerts" element={
                      <PrivateRoute>
                        <TradingviewDashboard />
                      </PrivateRoute>
                    } />

                    {/* New Admin Trading Routes */}
                    <Route path="/admin/folders" element={
                      <PrivateRoute roles={['admin']}>
                        <AdminFolders />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/indicator-alerts" element={
                      <PrivateRoute roles={['admin']}>
                        <AdminIndicatorAlerts />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/alerts" element={
                      <PrivateRoute roles={['admin']}>
                        <AdminAlerts />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/users" element={
                      <PrivateRoute roles={['admin']}>
                        <AdminUsers />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/terms" element={
                      <PrivateRoute roles={['admin']}>
                        <AdminTerms />
                      </PrivateRoute>
                    } />
                    <Route path="/admin/receiver" element={
                      <PrivateRoute roles={['admin']}>
                        <AlertReceiver />
                      </PrivateRoute>
                    } />

                    {/* Demo/Feature Routes */}
                    <Route path="/demo/wireframe" element={<WireframeDemo />} />
                    <Route path="/coder-ai" element={<CoderAI />} />
                    <Route path="/coder-ai/templates" element={<Templates />} />

                    {/* Payment Routes */}
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/failed" element={<PaymentFailed />} />
                    <Route path="/payment/checkout/:courseId" element={<PaymentCheckout />} />
                  </Routes>
                </LayoutWrapper>
                <ToastContainer position="top-right" autoClose={3000} />
              </React.Suspense>
            </Router>
          </SidebarProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
